import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import moment, { Moment } from 'moment'
import classnames from 'classnames'
const clock = require('static/clock@3x.png')

interface ITimeColumn {
  type: 'minute' | 'second' | 'hour'
  reverse?: boolean
  active?: number
  onSelect: (type: 'hour' | 'minute' | 'second', value: number) => void
  filtered: number[]
}

export interface ITime {
  hour: number
  minute: number
  second: number
}

interface ITimePicker {
  value: Moment
  onChange: (t: ITime) => void
  className?: string
  momentIsSelected: boolean
}

const hours: number[] = []
const minutes: number[] = []
const seconds: number[] = []
for (let i = 0; i <= 59; i++) {
  if (i <= 23) {
    hours.push(i)
  }
  minutes.push(i)
  seconds.push(i)
}

const TimeColumn = (props: ITimeColumn) => {
  const { type, reverse = false, active, onSelect, filtered } = props

  const collectionRef = useRef<HTMLUListElement>(null)
  let collection: number[]
  switch (type) {
  case 'hour':
    collection = hours
    break
  case 'minute':
    collection = minutes
    break
  case 'second':
    collection = seconds
    break
  default:
    collection = []
    break
  }
  if (reverse) {
    collection = collection.reverse()
  }
  useEffect(() => {
    if (collectionRef.current) {
      const activeLi = collectionRef.current.querySelector('.active')
      if (activeLi) {
        activeLi.scrollIntoView()
      }
    }
  }, [active])

  return (
    <div className="time-column">
      <ul className="collection" ref={collectionRef}>
        {collection.map((item, index) => (
          <li
            className={classnames(
              'item',
              item === active && 'active',
              filtered.includes(item) && 'disabled'
            )}
            key={index}
            onClick={() => {
              if (filtered.includes(item)) {
                return
              }
              onSelect(type, item)
            }}
          >
            {item < 10 ? `0${item}` : item}
          </li>
        ))}
      </ul>
      <style jsx>
        {`
          .time-column {
            width: 47px;
            height: 153px;
            overflow: hidden;
          }
          .collection {
            width: 70px;
            height: 100%;
            overflow-y: scroll;
            overflow-x: hidden;
            list-style-type: none;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
          }
          .item {
            flex: 0 0 auto;
            width: 47px;
            height: 25px;
            border-right: ${type === 'second' ? 'none' : '1px solid #e6e8f2'};
            box-sizing: border-box;
            display: flex;
            justify-content: center;
            align-items: center;
            font-size: 12px;
            color: #1f2129;
            cursor: pointer;
          }
          .item:hover {
            background-color: rgba(255, 204, 50, 0.4);
          }
          .item.active {
            border-right: none;
            border-radius: 2px;
            background-color: #ffcc32;
          }

          .disabled,
          .disabled:hover {
            color: #979aa8;
            cursor: not-allowed;
          }
        `}
      </style>
    </div>
  )
}

export default function TimePicker(props: ITimePicker) {
  const { value, onChange, momentIsSelected } = props
  const [hour, setHour] = useState<number>(0)
  const [minute, setMinute] = useState<number>(0)
  const [second, setSecond] = useState<number>(0)
  const fakeInputRef = useRef<HTMLDivElement | null>(null)
  const [open, setOpen] = useState(false)
  useEffect(() => {
    if (value) {
      setHour(value.hour())
      setMinute(value.minute())
      setSecond(value.second())
    }
  }, [value])

  const handleSelect = (type: 'hour' | 'minute' | 'second', value: number) => {
    switch (type) {
    case 'hour':
      setHour(value)
      if (minute && second) {
        onChange({ hour: value, minute, second })
      }
      break
    case 'minute':
      setMinute(value)
      if (hour && second) {
        onChange({ minute: value, hour, second })
      }
      break
    case 'second':
      setSecond(value)
      if (minute && hour) {
        onChange({ second: value, minute, hour })
      }
      break
    }
  }
  const handleFocus = useCallback(e => {
    setOpen(true)
  }, [])
  const handleContainerBlur = useCallback(() => {
    setOpen(false)
  }, [])
  const handleContainerFocus = useCallback(() => {
    if (fakeInputRef.current) {
      fakeInputRef.current.blur()
    }
  }, [])
  const handleIconClick = useCallback(e => {
    setOpen(true)
  }, [])
  const containerRef = (node: HTMLDivElement | null) => {
    if (!node) {
      return
    }
    if (fakeInputRef.current) {
      fakeInputRef.current.blur()
    }
    node.focus()
  }

  const [filteredHours, setFilteredHours] = useState<number[]>([])
  const [filteredMinutes, setFilteredMinutes] = useState<number[]>([])
  const [filteredSeconds, setFilteredSeconds] = useState<number[]>([])
  useEffect(() => {
    const tenMinLater = moment().add(10, 'minutes')
    const laterHour = tenMinLater.hour()
    const laterMin = tenMinLater.minute()
    const laterSec = tenMinLater.second()
    if (value.date() <= tenMinLater.date()) {
      setFilteredHours([...Array(laterHour).keys()])
      if (hour <= laterHour) {
        setFilteredMinutes([...Array(laterMin).keys()])
        if (minute <= laterMin) {
          setFilteredSeconds([...Array(laterSec).keys()])
        } else {
          setFilteredSeconds([])
        }
      } else {
        setFilteredMinutes([])
        setFilteredSeconds([])
      }
    } else {
      setFilteredHours([])
      setFilteredMinutes([])
      setFilteredSeconds([])
    }
  }, [value, hour, minute, second])

  return (
    <div className="time-picker">
      <div
        className="fake-input"
        onFocus={handleFocus}
        tabIndex={-1}
        ref={fakeInputRef}
      >
        <label className={momentIsSelected ? 'time' : 'default'}>
          {value?.format('HH:mm:ss')}
        </label>

        <img src={clock} className="time-icon" onClick={handleIconClick} />
      </div>
      {open && (
        <div
          className="container"
          onBlur={handleContainerBlur}
          tabIndex={-1}
          ref={containerRef}
          onFocus={handleContainerFocus}
        >
          <TimeColumn
            type="hour"
            active={hour}
            onSelect={handleSelect}
            filtered={filteredHours}
          />
          <TimeColumn
            type="minute"
            active={minute}
            onSelect={handleSelect}
            filtered={filteredMinutes}
          />
          <TimeColumn
            type="second"
            active={second}
            onSelect={handleSelect}
            filtered={filteredSeconds}
          />
        </div>
      )}

      <style jsx>
        {`
          .fake-input {
            width: 129px;
            height: 36px;
            border: solid 1px #c9cbd6;
            background-color: white;
            padding: 8px 16px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-radius: 2px;
            cursor: text;
            pointer-events: ${open ? 'none' : 'auto'};
          }
          .fake-input:hover {
            border: solid 1px #1f2129;
          }
          .fake-input:focus {
            border: solid 1px #1f2129;
          }
          .fake-input:focus,
          .container:focus {
            outline: none;
          }
          .default {
            color: #979aa8;
            cursor: text;
          }
          .time {
            color: #1f2129;
            cursor: text;
          }
          .time-icon {
            width: 16px;
            height: 16px;
            object-fit: cover;
            object-position: center;
            cursor: pointer;
          }
          .container {
            margin-top: 1px;
            position: absolute;
            display: flex;
            width: 142px;
            height: 153px;
            align-items: flex-start;
            border-radius: 2px;
            box-shadow: 0 6px 12px 0 rgba(0, 0, 0, 0.12);
            background-color: white;
          }
        `}
      </style>
    </div>
  )
}
